// File: frontend/src/hooks/useMatches.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { MatchModel } from "../models";
import { fetchMatchesByFilter } from "../services";
import { fetchMatchById } from "../services/matchService";
import { onMatchUpdated } from "../services/websocketService";
import { MatchGenderEnum } from "../models/enums/MatchGenderEnum";
import { SportTypeEnum } from "../models/enums/SportTypeEnum";
import { useTranslation } from "react-i18next";
import { MATCHES_PER_PAGE } from "../constants";
import { resolveRequestError } from "../utils/parseApiError";

type DateFilterType = "all" | "lastMonth" | "lastWeek" | "yesterday" | "today" | "tomorrow" | "thisWeek" | "month" | "custom";

// All available sport types for "ALL" selection
const ALL_SPORT_TYPES: SportTypeEnum[] = [
  SportTypeEnum.FOOTBALL,
  SportTypeEnum.BASKETBALL,
  SportTypeEnum.VOLLEYBALL,
  SportTypeEnum.AMERICAN_FOOTBALL,
  SportTypeEnum.BASEBALL,
  SportTypeEnum.ICE_HOCKEY,
  SportTypeEnum.HANDBALL,
];

// All available match genders for "ALL" selection
const ALL_MATCH_GENDERS: MatchGenderEnum[] = [
  MatchGenderEnum.MALE,
  MatchGenderEnum.FEMALE,
  MatchGenderEnum.MIXED,
];

export interface MatchFilter {
  sportTypes: SportTypeEnum[];
  matchGenders: MatchGenderEnum[];
  dateFilterType: DateFilterType;
  startDate?: Date;
  endDate?: Date;
  // Country filter — matches where either side's club belongs to the country.
  // null = all countries (no filter applied)
  country: string | null;
  // "Canlı" toggle — true narrows the list to matches currently in progress.
  // Deliberately NOT persisted to localStorage: a live match is over within
  // hours, so restoring the toggle on the next visit would only ever greet the
  // returning reader with an empty pane.
  liveOnly: boolean;
}

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

// v2: bumped when the default date window changed from "lastMonth" to "today".
// v3: bumped when the default date window changed from "today" to "all".
// Rationale: the left pane is an ACTIVITY list (sorted by last entry), not a
// fixture list. With a "today" window an entry written on an old match never
// surfaced in the pane. The filter auto-saves on mount, so every existing
// browser already had the previous default persisted — without a new key the
// new default would never take effect.
const FILTER_STORAGE_KEY = "matchFilter_v3";

// Helper functions with proper timezone handling
// Sets time to 00:00:00.000
const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper function to set time to end of day
// Sets time to 23:59:59.999
const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Serialise a local boundary instant as the UTC date-time the API expects
// ("YYYY-MM-DDTHH:mm:ss", no zone suffix — the backend reads it as UTC).
//
// startOfDay/endOfDay produce LOCAL day boundaries; converting them to UTC here is
// what makes the server-side window mean "the day as this viewer sees it". This
// replaces the old scheme of sending a ±1 day widened date window and then
// re-filtering the returned page in JavaScript: that second pass silently removed
// rows from a full page, so the pane showed fewer matches than the page size and
// totalItems no longer agreed with totalPages.
export const formatInstantForAPI = (date: Date | null): string | null => {
  if (!date) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  );
};

// Proper date formatting for API - uses local date, not UTC
export const formatDateForAPI = (date: Date | null) => {
  if (!date) return null;
  // Use local date components to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Load filter from localStorage
const loadFilterFromStorage = (): MatchFilter => {
  const defaultFilter: MatchFilter = { 
    sportTypes: [], 
    matchGenders: [],
    // Default date window is "all" — the match pane is an activity list: it is
    // sorted by last entry activity, so a match played months ago still has to
    // appear at the top when someone writes a new entry on it. A date window is
    // opt-in from the filter popover ("Bugün", "Bu Hafta", ...).
    dateFilterType: "all",
    country: null,
    liveOnly: false,
  };
  
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!stored) return defaultFilter;
    
    const parsed = JSON.parse(stored);
    
    // Validate sportTypes
    const sportTypes = Array.isArray(parsed.sportTypes) 
      ? parsed.sportTypes.filter((t: string) => ALL_SPORT_TYPES.includes(t as SportTypeEnum))
      : [];
    
    // Validate matchGenders
    const matchGenders = Array.isArray(parsed.matchGenders)
      ? parsed.matchGenders.filter((g: string) => ALL_MATCH_GENDERS.includes(g as MatchGenderEnum))
      : [];
    
    // Validate dateFilterType
    const validDateTypes: DateFilterType[] = ["all", "lastMonth", "lastWeek", "yesterday", "today", "tomorrow", "thisWeek", "month", "custom"];
    // Fallback must stay in sync with defaultFilter.dateFilterType above
    const dateFilterType = validDateTypes.includes(parsed.dateFilterType) 
      ? parsed.dateFilterType 
      : "all";
    
    // Parse custom dates
    const startDate = parsed.startDate ? new Date(parsed.startDate) : undefined;
    const endDate = parsed.endDate ? new Date(parsed.endDate) : undefined;

    // Validate country — must be a non-empty string, otherwise no filter
    const country = typeof parsed.country === "string" && parsed.country.trim() !== ""
      ? parsed.country
      : null;

    // liveOnly always starts off — see the field comment on MatchFilter
    return { sportTypes, matchGenders, dateFilterType, startDate, endDate, country, liveOnly: false };
  } catch {
    return defaultFilter;
  }
};

// Save filter to localStorage
const saveFilterToStorage = (filter: MatchFilter) => {
  const toStore = {
    sportTypes: filter.sportTypes,
    matchGenders: filter.matchGenders,
    dateFilterType: filter.dateFilterType,
    startDate: filter.startDate?.toISOString(),
    endDate: filter.endDate?.toISOString(),
    country: filter.country,
  };
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(toStore));
};

/**
 * Calculate date range for a given filter type.
 * Exported so MenuBar can use it for tooltips.
 */
export const getDateRangeForFilter = (
  dateFilterType: DateFilterType,
  customStartDate?: Date,
  customEndDate?: Date
): DateRange => {
  const today = new Date();
  let startDate: Date | null = null;
  let endDate: Date | null = null;

  switch (dateFilterType) {
    case "all":
      // No bounds — caller sends neither startDate nor endDate, and the
      // backend already treats missing dates as "no date filter" (see
      // MatchService.getAllMatchesByFilter).
      break;
    case "lastMonth":
      // Previous calendar month (e.g., November 1-30)
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      startDate = startOfDay(lastMonthStart);
      endDate = endOfDay(lastMonthEnd);
      break;
    case "lastWeek":
      // Previous calendar week (Monday to Sunday)
      const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ...
      const daysToLastMonday = currentDay === 0 ? 6 : currentDay - 1; // Days since this Monday
      const lastSunday = new Date(today);
      lastSunday.setDate(today.getDate() - daysToLastMonday - 1); // Last Sunday
      const lastMonday = new Date(lastSunday);
      lastMonday.setDate(lastSunday.getDate() - 6); // Last Monday
      startDate = startOfDay(lastMonday);
      endDate = endOfDay(lastSunday);
      break;
    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = startOfDay(yesterday);
      endDate = endOfDay(yesterday);
      break;
    case "today":
      startDate = startOfDay(today);
      endDate = endOfDay(today);
      break;
    case "tomorrow":
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      startDate = startOfDay(tomorrow);
      endDate = endOfDay(tomorrow);
      break;
    case "thisWeek":
      // Current calendar week (Monday to Sunday)
      const todayDay = today.getDay(); // 0 = Sunday, 1 = Monday, ...
      const daysFromMonday = todayDay === 0 ? 6 : todayDay - 1;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() - daysFromMonday);
      const thisSunday = new Date(thisMonday);
      thisSunday.setDate(thisMonday.getDate() + 6);
      startDate = startOfDay(thisMonday);
      endDate = endOfDay(thisSunday);
      break;
    case "month":
      // Current calendar month
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startDate = startOfDay(monthStart);
      endDate = endOfDay(monthEnd);
      break;
    case "custom":
      startDate = customStartDate ? startOfDay(customStartDate) : null;
      endDate = customEndDate ? endOfDay(customEndDate) : null;
      break;
  }

  return { startDate, endDate };
};

export { ALL_SPORT_TYPES, ALL_MATCH_GENDERS };

export const useMatches = (showRookieEntries: boolean = true, matchesPerPage: number = MATCHES_PER_PAGE) => {
  const { t } = useTranslation();

  const [matches, setMatches] = useState<MatchModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [filter, setFilter] = useState<MatchFilter>(loadFilterFromStorage);
  const [currentPage, setCurrentPage] = useState(1);
  // Sort mode: "lastEntryAt" = most recent entry first, "recentEntryCount" = most entries today first
  const [sortBy, setSortBy] = useState<"lastEntryAt" | "recentEntryCount">("lastEntryAt");

  // Loading state for the "load more" append operation
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Save filter to localStorage when it changes
  useEffect(() => {
    saveFilterToStorage(filter);
  }, [filter]);

  // Latest list, readable from the WebSocket handler without re-subscribing
  // on every list change
  const matchesRef = useRef<MatchModel[]>([]);
  useEffect(() => {
    matchesRef.current = matches;
  }, [matches]);

  // Per-match request counter: when two updates for the same match arrive in
  // quick succession, only the response of the latest request is applied, so a
  // slower older response can never overwrite a newer score.
  const liveRequestSeqRef = useRef<Map<number, number>>(new Map());

  // Real-time match updates pushed from the admin panel (score, status, lock...).
  // The changed match is re-read and patched IN PLACE: no loading overlay, no
  // reset of "load more" pages or scroll position. List membership and order
  // are not changed here — that still happens on the next manual refresh.
  useEffect(() => {
    const unsubscribe = onMatchUpdated(async ({ matchId }) => {
      if (!matchesRef.current.some((m) => m.id === matchId)) return;

      const seq = (liveRequestSeqRef.current.get(matchId) ?? 0) + 1;
      liveRequestSeqRef.current.set(matchId, seq);

      let fresh: MatchModel | null = null;
      try {
        fresh = await fetchMatchById(matchId);
      } catch {
        // Merged or unavailable match — keep the current row as it is
        return;
      }
      if (!fresh || liveRequestSeqRef.current.get(matchId) !== seq) return;

      const updated = fresh;
      setMatches((prev) =>
        prev.map((m) =>
          m.id === matchId
            ? {
                ...m,
                ...updated,
                // Entry counters depend on the viewer's rookie filter and the
                // list's day window — keep the values the list was loaded with
                entryCount: m.entryCount,
                recentEntryCount: m.recentEntryCount,
                lastEntryAt: m.lastEntryAt,
              }
            : m
        )
      );
    });
    return unsubscribe;
  }, []);

  // 1. Calculate the local-day date range for the active filter
  // 2. Send it to the API as a pair of UTC instants
  // 3. Update state with the page exactly as the server returned it
  //
  // There is deliberately NO client-side date filtering step any more: the day
  // bucketing rule now lives in SQL, so the server's page and its pagination
  // counters describe the same set of rows.
  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRangeForFilter(
        filter.dateFilterType,
        filter.startDate,
        filter.endDate
      );

      // If sportTypes/matchGenders is empty, don't send any filter (backend returns all)
      const response = await fetchMatchesByFilter(
        filter.sportTypes,
        filter.matchGenders,
        formatInstantForAPI(startDate),
        formatInstantForAPI(endDate),
        filter.country,
        filter.liveOnly,
        currentPage,
        showRookieEntries,
        sortBy,
        matchesPerPage
      );

      // Replace mode: always replace the current list when navigating pages or changing filters
      setMatches(response.matches);
      // Pagination is taken verbatim from the server. totalItems must stay the total
      // number of matching rows, not the number of rows on this page — the pager
      // renders totalItems and totalPages together and they have to agree.
      setPagination(response.pagination);
    } catch (error: any) {
      // Route through resolveRequestError so a dropped connection shows the
      // localized "İnternet bağlantın kopmuş…" message instead of leaking the
      // browser's raw English "Failed to fetch" TypeError text.
      setError(resolveRequestError(error, t));
    } finally {
      setLoading(false);
    }
  }, [filter, currentPage, showRookieEntries, sortBy, matchesPerPage, t]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  /**
   * Fetches the next page of matches and APPENDS them to the current list.
   * Does NOT change currentPage state so it won't trigger a replace-load.
   * Called when the user clicks "daha da..." in the left pane.
   */
  const loadMoreMatches = useCallback(async () => {
    const nextPage = pagination.currentPage + 1;
    if (isLoadingMore || nextPage > pagination.totalPages) return;

    setIsLoadingMore(true);
    try {
      const { startDate, endDate } = getDateRangeForFilter(
        filter.dateFilterType,
        filter.startDate,
        filter.endDate
      );

      const response = await fetchMatchesByFilter(
        filter.sportTypes,
        filter.matchGenders,
        formatInstantForAPI(startDate),
        formatInstantForAPI(endDate),
        filter.country,
        filter.liveOnly,
        nextPage,
        showRookieEntries,
        sortBy,
        matchesPerPage
      );

      // Append new matches to the existing list — the server page is already final
      setMatches((prev) => [...prev, ...response.matches]);

      // Advance the pagination page counter without triggering a replace-load.
      // totalItems/totalPages are refreshed from the server so the "daha da..."
      // affordance disappears on the true last page.
      setPagination((prev) => ({
        ...prev,
        currentPage: nextPage,
        totalPages: response.pagination.totalPages,
        totalItems: response.pagination.totalItems,
      }));
    } catch {
      // Silent failure — the existing list remains intact
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, pagination, filter, showRookieEntries, sortBy, matchesPerPage]);

  const applyFilter = (newFilter: Partial<MatchFilter>) => {
    setCurrentPage(1);
    setFilter((prev) => ({ ...prev, ...newFilter }));
  };

  const paginateMatches = (page: number) => {
    setCurrentPage(page);
  };

  return {
    matches,
    loading,
    error,
    pagination,
    loadMatches: paginateMatches,
    refreshMatches: loadMatches,  // Allows manual refresh without changing page
    loadMoreMatches,
    isLoadingMore,
    filter,
    applyFilter,
    sortBy,
    setSortBy,
  };
};